import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import {
  getMyAlertTiers,
  saveAlertTiers,
  sendTestAlert,
  MyAlertTiers,
} from '../../../api/payment/donate-alert-tiers.api';
import {
  getMyAlertAssets,
  deleteAlertAsset,
  uploadAlertAsset,
  AlertAsset,
} from '../../../api/payment/donate-alert-assets.api';
import { AlertTier, formatCa, libFile, renderTemplate, tierSoundSrc, tierImageSrc } from '../../../utils/donate-alert';
import {
  ALERT_KINDS,
  AlertKind,
  KIND_LABELS,
  KIND_PRESETS,
  TTS_VOICES,
  applyPresetKinds,
  normalizeKind,
  overrideFor,
  resolveAlertKind,
} from '../../../utils/alert-kind';

const SOUND_FILES = Array.from({ length: 15 }, (_, i) => `sound_${i + 1}.mp3`);
const GIF_FILES = [...Array.from({ length: 12 }, (_, i) => `img_${i + 1}.gif`), 'img13.gif'];
const PLACEHOLDERS = ['{ten}', '{so_tien}', '{loi_nhan}'];
const MP3_MAX_BYTES = 3 * 1024 * 1024;
const GIF_MAX_BYTES = 5 * 1024 * 1024;
const INPUT =
  'h-11 w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white';
const BTN_SECONDARY =
  'h-10 rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 text-sm text-gray-700 dark:text-gray-200';

const newTier = (tiers: AlertTier[]): AlertTier => ({
  minAmount: tiers.reduce((m, t) => Math.max(m, Number(t.minAmount) || 0), 0) + 50000,
  sound: 'lib:sound_1.mp3',
  image: 'lib:img_1.gif',
  durationSec: 8,
  textColor: null,
  readAloud: true,
  template: null,
  kind: 'normal',
  introEnabled: null,
  medalEnabled: null,
  effectEnabled: null,
  voice: null,
});

const KIND_HINTS: Record<AlertKind, { boost: string; extra: string | null; intro: string; medal: string; effect: string; voice: string | null }> = {
  normal: {
    boost: 'Kiểu này kêu đúng mức âm lượng bạn đặt.',
    extra: null,
    intro: 'Một đoạn nhạc ngắn vang lên trước tiếng thông báo.',
    medal: 'Huy hiệu bạc hiện cạnh tên người ủng hộ.',
    effect: 'Viền phát sáng quanh thông báo.',
    voice: null,
  },
  highlight: {
    boost: 'Kiểu này kêu to hơn mức bạn đặt 10 điểm.',
    extra: 'Thông báo ở lại trên màn hình thêm 3 giây.',
    intro: 'Một đoạn nhạc ngắn vang lên trước tiếng thông báo.',
    medal: 'Huy hiệu bạc hiện cạnh tên người ủng hộ.',
    effect: 'Viền phát sáng quanh thông báo.',
    voice: 'Kiểu này đọc chậm hơn một chút.',
  },
  special: {
    boost: 'Kiểu này kêu to hơn mức bạn đặt 20 điểm.',
    extra: 'Thông báo ở lại trên màn hình thêm 6 giây.',
    intro: 'Một đoạn nhạc dài hơn vang lên trước tiếng thông báo.',
    medal: 'Huy hiệu vàng hiện cạnh tên người ủng hộ.',
    effect: 'Pháo hoa bắn khắp màn hình.',
    voice: 'Kiểu này đọc chậm và ngắt nghỉ giữa tên, số Ca và lời nhắn.',
  },
};

type KindPatch = Pick<AlertTier, 'kind' | 'introEnabled' | 'medalEnabled' | 'effectEnabled' | 'voice'>;

/** Chọn kiểu thông báo của một mốc và các lựa chọn riêng (bắt đầu từ bộ cài sẵn của kiểu). */
const TierKindEditor: React.FC<{
  tier: AlertTier;
  locked: boolean;
  onChange: (patch: Partial<KindPatch>) => void;
}> = ({ tier, locked, onChange }) => {
  const kind = normalizeKind(tier.kind);
  const preset = KIND_PRESETS[kind];
  const resolved = resolveAlertKind(tier);
  const hints = KIND_HINTS[kind];
  const pickKind = (k: AlertKind) =>
    onChange({ kind: k, introEnabled: null, medalEnabled: null, effectEnabled: null, voice: null });
  const toggles: { key: 'introEnabled' | 'medalEnabled' | 'effectEnabled'; label: string; hint: string; on: boolean; presetOn: boolean }[] =
    [
      { key: 'introEnabled', label: 'Nhạc mở màn', hint: hints.intro, on: !!resolved.intro, presetOn: !!preset.intro },
      { key: 'medalEnabled', label: 'Huy hiệu', hint: hints.medal, on: !!resolved.medal, presetOn: !!preset.medal },
      { key: 'effectEnabled', label: 'Hiệu ứng màn hình', hint: hints.effect, on: !!resolved.effect, presetOn: !!preset.effect },
    ];

  return (
    <div className="flex flex-col gap-2 text-sm dark:text-gray-200">
      <span>Kiểu thông báo</span>
      <div role="radiogroup" aria-label="Kiểu thông báo" className="flex flex-wrap gap-2">
        {ALERT_KINDS.map((k) => {
          const selected = kind === k;
          const disabled = locked && k !== 'normal';
          return (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => pickKind(k)}
              className={`h-10 rounded-lg border px-4 text-sm ${
                selected
                  ? 'border-blue-600 bg-blue-50 font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-200'
                  : 'border-[#C8CCD8] text-gray-700 dark:border-slate-600 dark:text-gray-200'
              } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              {KIND_LABELS[k]}
            </button>
          );
        })}
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400">{hints.boost}</span>
      {hints.extra && <span className="text-xs text-gray-500 dark:text-gray-400">{hints.extra}</span>}

      {locked ? (
        <span className="text-xs text-amber-600">
          {kind === 'normal'
            ? 'Kiểu Nổi bật và Đặc biệt dành cho tài khoản có CAB+.'
            : 'Kiểu này cần CAB+ nên OBS đang hiện mốc này như kiểu Thường. Kiểu bạn đã chọn vẫn được giữ và chạy lại khi bạn có CAB+.'}{' '}
          <Link to="/account/cabplus" className="text-blue-600 underline">
            Xem CAB+
          </Link>
        </span>
      ) : (
        <div className="flex flex-col gap-3 rounded-lg bg-[#f6f7f8] p-3 dark:bg-slate-700/40">
          {toggles.map((t) => (
            <label key={t.key} className="flex cursor-pointer select-none items-start gap-2">
              <input
                type="checkbox"
                checked={t.on}
                onChange={(e) => onChange({ [t.key]: overrideFor(e.target.checked, t.presetOn) })}
                className="mt-0.5 h-4 w-4 rounded border-[#C8CCD8] accent-blue-600"
              />
              <span className="flex flex-col">
                <span>{t.label}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{t.hint}</span>
              </span>
            </label>
          ))}
          <label className="flex flex-col gap-1">
            Giọng đọc
            <select
              value={resolved.voice}
              onChange={(e) => onChange({ voice: e.target.value === preset.voice ? null : e.target.value })}
              className={INPUT}
            >
              {TTS_VOICES.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
            {hints.voice && <span className="text-xs text-gray-500 dark:text-gray-400">{hints.voice}</span>}
          </label>
        </div>
      )}
    </div>
  );
};

/**
 * Hiệu ứng theo mốc donate: mỗi mức tiền một hiệu ứng riêng trên OBS.
 * Donate thấp hơn mốc nhỏ nhất vẫn dùng hiệu ứng mặc định phía trên.
 */
const DonateAlertTiersSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const [tiers, setTiers] = useState<AlertTier[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  const [maxTiers, setMaxTiers] = useState(3);
  const [kindsLocked, setKindsLocked] = useState(false);
  // Creator chưa có mốc nào: mỗi lần thêm, xoá hay đổi số Ca thì tự xếp kiểu cài sẵn (cao nhất Đặc biệt, kế tiếp Nổi bật)
  // cho tới khi creator tự chọn kiểu. Mốc đã lưu thì không tự đổi.
  const [autoPreset, setAutoPreset] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gifPickerFor, setGifPickerFor] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [assets, setAssets] = useState<AlertAsset[]>([]);
  const [assetsMax, setAssetsMax] = useState(0);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const savedTiersRef = useRef<AlertTier[]>([]);
  const soundInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const imageInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const apply = (res: MyAlertTiers) => {
    savedTiersRef.current = res?.tiers || [];
    setTiers(res?.tiers || []);
    setIsPremium(!!res?.isPremium);
    setMaxTiers(res?.maxTiers || 3);
    setKindsLocked(!!res?.kindsLocked);
    setAutoPreset(!res?.kindsLocked && (res?.tiers || []).length === 0);
  };

  const loadAssets = () => {
    getMyAlertAssets()
      .then((res) => {
        setAssets(res?.assets || []);
        setAssetsMax(res?.maxAssets || 0);
      })
      .catch(() => setAssets([]));
  };

  const load = () => {
    setLoadError(false);
    getMyAlertTiers()
      .then((res) => {
        apply(res);
        setLoaded(true);
      })
      .catch(() => setLoadError(true));
    loadAssets();
  };

  useEffect(() => {
    if (!userId) return;
    load();
  }, [userId]);

  const update = (index: number, patch: Partial<AlertTier>) =>
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));

  // Thay đổi làm đổi thứ hạng số Ca: đang ở chế độ tự xếp thì xếp lại kiểu cài sẵn.
  const changeList = (fn: (prev: AlertTier[]) => AlertTier[]) =>
    setTiers((prev) => {
      const next = fn(prev);
      return autoPreset ? applyPresetKinds(next) : next;
    });

  const updateKind = (index: number, patch: Partial<AlertTier>) => {
    setAutoPreset(false);
    update(index, patch);
  };

  const remove = (index: number) => {
    changeList((prev) => prev.filter((_, i) => i !== index));
    setGifPickerFor(null);
  };

  const applyPresetToAll = () => {
    if (
      !window.confirm(
        'Mốc cao nhất sẽ dùng kiểu Đặc biệt, mốc kế tiếp dùng kiểu Nổi bật, các mốc còn lại dùng kiểu Thường. Lựa chọn riêng của từng mốc sẽ được đặt lại. Bạn vẫn cần bấm Lưu hiệu ứng để áp dụng.'
      )
    )
      return;
    setTiers((prev) => applyPresetKinds(prev));
  };

  const insertPlaceholder = (index: number, ph: string) => {
    const cur = tiers[index]?.template || '';
    const next = `${cur}${cur && !cur.endsWith(' ') ? ' ' : ''}${ph}`;
    if (next.length > 120) {
      toast.error('Câu đã đủ 120 ký tự');
      return;
    }
    update(index, { template: next });
  };

  const isDirty = JSON.stringify(tiers) !== JSON.stringify(savedTiersRef.current);

  const previewSound = (src: string | null) => {
    if (!src) return;
    const a = new Audio(src);
    a.play().catch(() => undefined);
  };

  const openPreview = (t: AlertTier) => {
    const q = new URLSearchParams({
      isTest: 'true',
      duration: String(t.durationSec),
      readAloud: String(t.readAloud),
    });
    const soundSrc = t.sound.startsWith('up:') ? tierSoundSrc(t) : null;
    if (soundSrc) {
      q.set('soundUrl', soundSrc);
    } else {
      q.set('sound', libFile(t.sound) || 'sound_1.mp3');
    }
    const imageSrc = t.image.startsWith('up:') ? tierImageSrc(t) : null;
    if (imageSrc) {
      q.set('imageUrl', imageSrc);
    } else {
      q.set('image', libFile(t.image) || 'img_1.gif');
    }
    if (t.textColor) q.set('colorText', t.textColor.replace('#', ''));
    if (t.template) q.set('template', t.template);
    q.set('kind', normalizeKind(t.kind));
    const flag = (key: string, v: boolean | null | undefined) => {
      if (typeof v === 'boolean') q.set(key, v ? '1' : '0');
    };
    flag('intro', t.introEnabled);
    flag('medal', t.medalEnabled);
    flag('effect', t.effectEnabled);
    if (t.voice) q.set('voice', t.voice);
    window.open(`/donate-screen/${userId}?${q.toString()}`, 'popup', 'width=700,height=420,top=100,left=100');
  };

  const handleUploadSound = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    e.target.value = '';
    if (!file) return;
    if (!/\.mp3$/i.test(file.name)) {
      toast.error('Chỉ nhận file .mp3.');
      return;
    }
    if (file.size > MP3_MAX_BYTES) {
      toast.error('Bài nhạc tối đa 3MB.');
      return;
    }
    if (assets.length >= assetsMax) {
      toast.error(`Bạn đã có đủ ${assetsMax} file. Xoá bớt rồi tải tiếp nhé.`);
      return;
    }
    setUploading((prev) => ({ ...prev, [`${index}-sound`]: true }));
    uploadAlertAsset(file)
      .then((asset) => {
        setAssets((prev) => [asset, ...prev]);
        update(index, { sound: `up:${asset.id}`, soundUrl: asset.url });
        toast.success('Đã tải lên, nhớ bấm Lưu hiệu ứng nhé');
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Tải lên không thành công'))
      .finally(() => setUploading((prev) => ({ ...prev, [`${index}-sound`]: false })));
  };

  const handleUploadImage = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    e.target.value = '';
    if (!file) return;
    if (!/\.gif$/i.test(file.name)) {
      toast.error('Chỉ nhận file .gif.');
      return;
    }
    if (file.size > GIF_MAX_BYTES) {
      toast.error('Ảnh động tối đa 5MB.');
      return;
    }
    if (assets.length >= assetsMax) {
      toast.error(`Bạn đã có đủ ${assetsMax} file. Xoá bớt rồi tải tiếp nhé.`);
      return;
    }
    setUploading((prev) => ({ ...prev, [`${index}-image`]: true }));
    uploadAlertAsset(file)
      .then((asset) => {
        setAssets((prev) => [asset, ...prev]);
        update(index, { image: `up:${asset.id}`, imageUrl: asset.url });
        toast.success('Đã tải lên, nhớ bấm Lưu hiệu ứng nhé');
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Tải lên không thành công'))
      .finally(() => setUploading((prev) => ({ ...prev, [`${index}-image`]: false })));
  };

  const handleDeleteAsset = (id: string) => {
    if (!window.confirm('Xoá file này? Mốc đang dùng sẽ trở về âm thanh, ảnh mặc định.')) return;
    deleteAlertAsset(id)
      .then(() => {
        setAssets((prev) => prev.filter((a) => a.id !== id));
        // Nếu form đang có sửa dở chưa lưu thì không nạp lại từ server (mất sửa dở);
        // chỉ vá field liên quan tới file vừa xoá ở cả bản đang sửa lẫn bản đã lưu,
        // để dirty-check không bị lệch do việc xoá này.
        if (isDirty) {
          const patch = (list: AlertTier[]) =>
            list.map((t) => ({
              ...t,
              sound: t.sound === `up:${id}` ? 'lib:sound_1.mp3' : t.sound,
              soundUrl: t.sound === `up:${id}` ? null : t.soundUrl,
              image: t.image === `up:${id}` ? 'lib:img_1.gif' : t.image,
              imageUrl: t.image === `up:${id}` ? null : t.imageUrl,
            }));
          setTiers((prev) => patch(prev));
          savedTiersRef.current = patch(savedTiersRef.current);
        } else {
          getMyAlertTiers().then(apply).catch(() => undefined);
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Xoá không thành công'));
  };

  const testOnObs = (t: AlertTier) => {
    if (isDirty) {
      toast.error('Bạn lưu thay đổi trước rồi gửi thử nhé');
      return;
    }
    sendTestAlert(Number(t.minAmount))
      .then(() => toast.success('Đã gửi, xem trên OBS nhé'))
      .catch((err) => toast.error(err?.response?.data?.message || 'Gửi thử không thành công'));
  };

  const handleSave = () => {
    if (saving || !loaded) return;
    setSaving(true);
    saveAlertTiers(tiers.map((t) => ({ ...t, minAmount: Number(t.minAmount) || 0 })))
      .then((res) => {
        apply(res);
        toast.success('Đã lưu hiệu ứng theo mốc');
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Lưu không thành công'))
      .finally(() => setSaving(false));
  };

  const canAdd = loaded && tiers.length < maxTiers;
  const anyUploading = Object.values(uploading).some(Boolean);

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Sparkles size={20} className="text-blue-600" />
        Hiệu ứng theo mốc donate
      </p>

      <div className="w-full flex flex-col gap-3 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Donate từ mức nào trở lên thì phát hiệu ứng của mốc đó. Donate thấp hơn mốc nhỏ nhất vẫn dùng
          hiệu ứng mặc định ở trên.
        </p>

        {loadError && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            <span>Không tải được danh sách mốc, bạn thử lại nhé.</span>
            <button type="button" className="font-medium underline" onClick={load}>
              Thử lại
            </button>
          </div>
        )}

        {tiers.map((t, i) => (
          <div
            key={i}
            className={`rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-3 flex flex-col gap-3 ${
              t.active === false ? 'opacity-60' : ''
            }`}
          >
            {t.active === false && (
              <p className="text-xs font-medium text-amber-600">Cần CAB+ để bật lại mốc này</p>
            )}

            {!isPremium && (t.sound.startsWith('up:') || t.image.startsWith('up:')) && (
              <p className="text-xs font-medium text-amber-600">
                File riêng đang tạm tắt vì chưa có CAB+. Bạn chọn âm thanh, ảnh có sẵn để lưu được.
              </p>
            )}

            <label className="flex flex-col gap-1 text-sm dark:text-gray-200">
              Số Ca tối thiểu
              <input
                type="number"
                min={1}
                value={t.minAmount}
                onChange={(e) => {
                  const minAmount = Math.max(0, Math.floor(Number(e.target.value) || 0));
                  changeList((prev) => prev.map((x, idx) => (idx === i ? { ...x, minAmount } : x)));
                }}
                className={INPUT}
              />
            </label>

            <TierKindEditor tier={t} locked={kindsLocked} onChange={(patch) => updateKind(i, patch)} />

            <label className="flex flex-col gap-1 text-sm dark:text-gray-200">
              Âm thanh
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={t.sound}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.startsWith('up:')) {
                      const asset = assets.find((a) => `up:${a.id}` === v);
                      update(i, { sound: v, soundUrl: asset?.url ?? null });
                    } else {
                      update(i, { sound: v, soundUrl: null });
                    }
                  }}
                  className={INPUT}
                >
                  {SOUND_FILES.map((f, idx) => (
                    <option key={f} value={`lib:${f}`}>
                      Âm thanh {idx + 1}
                    </option>
                  ))}
                  {isPremium ? (
                    <>
                      {assets.some((a) => a.kind === 'audio') && (
                        <optgroup label="Bài bạn đã tải lên">
                          {assets
                            .filter((a) => a.kind === 'audio')
                            .map((a) => (
                              <option key={a.id} value={`up:${a.id}`}>
                                {a.originalName || 'Bài của bạn'}
                              </option>
                            ))}
                        </optgroup>
                      )}
                      {t.sound.startsWith('up:') && !assets.some((a) => `up:${a.id}` === t.sound) && (
                        <option value={t.sound}>Bài đã bị xoá</option>
                      )}
                    </>
                  ) : (
                    t.sound.startsWith('up:') && <option value={t.sound}>File riêng (cần CAB+)</option>
                  )}
                </select>
                <button
                  type="button"
                  className={BTN_SECONDARY}
                  onClick={() => previewSound(tierSoundSrc(t))}
                >
                  Nghe
                </button>
                {isPremium ? (
                  <>
                    <button
                      type="button"
                      className={BTN_SECONDARY}
                      disabled={!!uploading[`${i}-sound`]}
                      onClick={() => soundInputRefs.current[i]?.click()}
                    >
                      {uploading[`${i}-sound`] ? 'Đang tải…' : 'Tải bài của bạn'}
                    </button>
                    <input
                      hidden
                      type="file"
                      accept=".mp3,audio/mpeg"
                      ref={(el) => {
                        soundInputRefs.current[i] = el;
                      }}
                      onChange={(e) => handleUploadSound(i, e)}
                    />
                  </>
                ) : (
                  <Link to="/account/cabplus" className="text-xs text-blue-600 underline">
                    Tải nhạc và ảnh riêng dành cho CAB+
                  </Link>
                )}
              </div>
            </label>

            <div className="flex flex-col gap-1 text-sm dark:text-gray-200">
              Ảnh động
              <div className="flex flex-wrap items-center gap-3">
                <img
                  src={tierImageSrc(t) || '/gif/img_1.gif'}
                  alt=""
                  className="h-16 w-16 rounded object-contain bg-gray-100 dark:bg-slate-700"
                />
                <button
                  type="button"
                  className={BTN_SECONDARY}
                  onClick={() => setGifPickerFor(gifPickerFor === i ? null : i)}
                >
                  {gifPickerFor === i ? 'Đóng' : 'Đổi ảnh'}
                </button>
                {isPremium ? (
                  <>
                    <button
                      type="button"
                      className={BTN_SECONDARY}
                      disabled={!!uploading[`${i}-image`]}
                      onClick={() => imageInputRefs.current[i]?.click()}
                    >
                      {uploading[`${i}-image`] ? 'Đang tải…' : 'Tải ảnh của bạn'}
                    </button>
                    <input
                      hidden
                      type="file"
                      accept=".gif,image/gif"
                      ref={(el) => {
                        imageInputRefs.current[i] = el;
                      }}
                      onChange={(e) => handleUploadImage(i, e)}
                    />
                  </>
                ) : (
                  <Link to="/account/cabplus" className="text-xs text-blue-600 underline">
                    Tải nhạc và ảnh riêng dành cho CAB+
                  </Link>
                )}
              </div>
              {gifPickerFor === i && (
                <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-56 overflow-y-auto">
                  {GIF_FILES.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => {
                        update(i, { image: `lib:${f}`, imageUrl: null });
                        setGifPickerFor(null);
                      }}
                      className={`rounded border p-1 ${
                        libFile(t.image) === f ? 'border-blue-600' : 'border-transparent'
                      }`}
                    >
                      <img src={`/gif/${f}`} alt="" className="h-14 w-full object-contain" />
                    </button>
                  ))}
                  {isPremium &&
                    assets
                      .filter((a) => a.kind === 'gif')
                      .map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => {
                            update(i, { image: `up:${a.id}`, imageUrl: a.url });
                            setGifPickerFor(null);
                          }}
                          className={`rounded border p-1 ${
                            t.image === `up:${a.id}` ? 'border-blue-600' : 'border-transparent'
                          }`}
                        >
                          <img src={a.url} alt="" className="h-14 w-full object-contain" />
                        </button>
                      ))}
                </div>
              )}
            </div>

            <label className="flex flex-col gap-1 text-sm dark:text-gray-200">
              Thời gian hiện: {t.durationSec} giây
              <input
                type="range"
                min={5}
                max={30}
                value={t.durationSec}
                onChange={(e) => update(i, { durationSec: Number(e.target.value) })}
                className="accent-blue-600"
              />
            </label>

            <div className="flex flex-wrap items-center gap-3 text-sm dark:text-gray-200">
              <span>Màu chữ</span>
              <input
                type="color"
                value={t.textColor || '#ff0000'}
                onChange={(e) => update(i, { textColor: e.target.value })}
                className="h-9 w-12 rounded border border-[#C8CCD8] dark:border-slate-600"
              />
              {t.textColor && (
                <button
                  type="button"
                  className="text-xs text-blue-600 underline"
                  onClick={() => update(i, { textColor: null })}
                >
                  Dùng màu mặc định
                </button>
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={t.readAloud}
                onChange={(e) => update(i, { readAloud: e.target.checked })}
                className="h-4 w-4 rounded border-[#C8CCD8] accent-blue-600"
              />
              Đọc to tên và lời nhắn
            </label>

            <label className="flex flex-col gap-1 text-sm dark:text-gray-200">
              Câu hiển thị
              <input
                value={t.template || ''}
                maxLength={120}
                placeholder="VD: {ten} vừa bao cả phòng {so_tien}!"
                onChange={(e) => update(i, { template: e.target.value || null })}
                className={INPUT}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">Để trống thì dùng câu mặc định.</span>
              <div className="flex flex-wrap gap-2">
                {PLACEHOLDERS.map((ph) => (
                  <button
                    key={ph}
                    type="button"
                    className="rounded bg-gray-100 dark:bg-slate-700 px-2 py-1 text-xs"
                    onClick={() => insertPlaceholder(i, ph)}
                  >
                    {ph}
                  </button>
                ))}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Xem trước:{' '}
                {renderTemplate(t.template, {
                  ten: 'Minh',
                  soTien: Number(t.minAmount) || 0,
                  loiNhan: 'Chúc stream vui',
                })}
              </span>
            </label>

            <div className="flex flex-wrap gap-2">
              <button type="button" className={BTN_SECONDARY} onClick={() => openPreview(t)}>
                Xem thử
              </button>
              <button type="button" className={BTN_SECONDARY} onClick={() => testOnObs(t)}>
                Gửi thử lên OBS
              </button>
              <button
                type="button"
                disabled={anyUploading}
                className={`h-10 rounded-lg px-3 text-sm text-red-600 ${
                  anyUploading ? 'cursor-not-allowed opacity-50' : ''
                }`}
                onClick={() => remove(i)}
              >
                Xoá
              </button>
            </div>
          </div>
        ))}

        {tiers.length === 0 && loaded && !loadError && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Bạn chưa có mốc nào. Mọi donate đang dùng hiệu ứng mặc định.
          </p>
        )}

        {assets.length > 0 && (
          <div className="flex flex-col gap-2 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
            <p className="text-sm font-medium text-[#18191E] dark:text-white">File của bạn</p>
            <ul className="flex flex-col gap-2">
              {assets.map((a) => (
                <li
                  key={a.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-700 dark:text-gray-200"
                >
                  <span>
                    {a.originalName || (a.kind === 'audio' ? 'Bài của bạn' : 'Ảnh của bạn')}{' '}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      · {a.kind === 'audio' ? 'Bài nhạc' : 'Ảnh động'}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="text-xs text-red-600 underline"
                    onClick={() => handleDeleteAsset(a.id)}
                  >
                    Xoá
                  </button>
                </li>
              ))}
            </ul>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Đã dùng {assets.length}/{assetsMax} file
            </span>
            {!isPremium && (
              <span className="text-xs text-amber-600">Cần CAB+ để dùng lại các file này.</span>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={!canAdd}
            onClick={() => changeList((prev) => [...prev, newTier(prev)])}
            className={`${BTN_SECONDARY} ${!canAdd ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            Thêm mốc
          </button>
          {loaded && !kindsLocked && tiers.length > 0 && (
            <button type="button" onClick={applyPresetToAll} className={BTN_SECONDARY}>
              Dùng kiểu cài sẵn
            </button>
          )}
          {loaded && tiers.length >= maxTiers && !isPremium && (
            <span className="text-xs text-gray-600 dark:text-gray-300">
              Nâng{' '}
              <Link to="/account/cabplus" className="text-blue-600 underline">
                CAB+
              </Link>{' '}
              để có tới 10 mốc.
            </span>
          )}
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {tiers.length}/{maxTiers} mốc · mốc nhỏ nhất{' '}
            {tiers.length ? formatCa(Math.min(...tiers.map((x) => Number(x.minAmount) || 0))) : 'chưa có'}
          </span>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={saving || !loaded}
            onClick={handleSave}
            className="flex h-12 items-center gap-1 rounded-lg bg-[#39539E] px-6 text-white disabled:opacity-60"
          >
            {saving ? 'Đang lưu…' : 'Lưu hiệu ứng'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DonateAlertTiersSetting;
